import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check, AlertTriangle, Repeat } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

function time12(t) {
  if (!t) return "9:00 AM";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

export function TasksBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: items = [] } = useQuery({
    queryKey: ["follow-ups", "due-bell"],
    queryFn: async () => (await api.get("/follow-ups", { params: { status: "pending" } })).data,
    refetchInterval: 30000,
  });

  const todayStr = new Date().toLocaleDateString("en-CA");
  const due = items
    .filter((i) => i.status !== "done" && (i.overdue || i.due_date === todayStr))
    .sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
  const count = due.length;

  const markDone = async (id) => {
    try {
      await api.post(`/follow-ups/${id}/done`);
      qc.invalidateQueries({ queryKey: ["follow-ups"] });
      qc.invalidateQueries({ queryKey: ["reminders"] });
      toast.success("Task completed");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors" data-testid="tasks-bell" title="Tasks due today">
          <CalendarClock className="h-[18px] w-[18px] text-slate-600" />
          {count > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center" data-testid="tasks-due-count">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="tasks-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-heading font-semibold text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-brand" /> Tasks due today</span>
          <span className="text-[11px] text-slate-400">{count} pending</span>
        </div>
        <ScrollArea className="max-h-[380px]">
          {count === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400">You're all caught up — no tasks due today.</div>}
          {due.map((f) => (
            <div key={f.id} className="px-4 py-3 border-b last:border-0 flex gap-3 hover:bg-slate-50 transition-colors" data-testid={`task-due-${f.id}`}>
              <button onClick={() => navigate(f.link)} className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium text-slate-800 truncate">{f.note}</p>
                <p className="text-xs text-slate-500 truncate">{f.company || f.entity_name}</p>
                <p className={`text-[11px] font-medium mt-0.5 flex items-center gap-1 ${f.overdue ? "text-red-600" : "text-slate-500"}`}>
                  {f.overdue && <AlertTriangle className="h-3 w-3" />}
                  {formatDate(f.due_date)} · {time12(f.due_time)}{f.overdue ? " · overdue" : ""}
                  {f.recurrence && f.recurrence !== "none" && <Repeat className="h-2.5 w-2.5 text-brand" />}
                </p>
              </button>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0 flex-shrink-0 self-center" onClick={() => markDone(f.id)} title="Mark done" data-testid={`task-done-${f.id}`}>
                <Check className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
